import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import ErrorBoundary from '../components/ErrorBoundary';
import { retryWithBackoff, getErrorMessage, getFallbackResponse } from '../utils/aiHelpers';
import { sortChaptersByCurriculumOrder } from '../utils/courseLock';
import logger from '../utils/logger';
import { exportFeedbackAsPdf } from '../utils/pdfExport';

// Helper function to clean chapter titles (optional, but good practice)
const formatDisplayTitle = (title) => {
  if (!title) return '';
  const prefixRegex = /^(workshop|chapter|module)\s?\d+:\s*/i;
  return title.replace(prefixRegex, '').trim();
};

/** Parse SSE stream from an existing Response body (avoids double-fetch when GET already returned a stream). */
async function readStreamFromResponse(response, onChunk, onComplete, onError) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let streamedFeedback = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        let data = null;
        try {
          data = JSON.parse(line.slice(6));
          if (data.type === 'chunk' && data.content) {
            streamedFeedback += data.content;
            onChunk(streamedFeedback);
          } else if (data.type === 'complete') {
            onComplete(streamedFeedback);
            return;
          } else if (data.type === 'error') {
            throw new Error(data.error || 'Streaming error');
          }
        } catch (e) {
          if (data?.type === 'error') throw e;
        }
      }
    }
    onComplete(streamedFeedback);
  } catch (err) {
    onError(err);
  }
}

function Feedback({ setCurrentFeedback }) {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, token, user, authLoading } = useContext(AuthContext);
  const [feedback, setFeedback] = useState(null);
  const [chapterTitle, setChapterTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [chapters, setChapters] = useState([]); // Add chapters state for navigation
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState([]); // Store Q&A for display
  const [chapterData, setChapterData] = useState(null); // Store chapter data for Q&A

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated === false) {
      // Save current location so we can redirect back after login
      navigate('/login', { 
        state: { from: location },
        replace: true 
      });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  // Function to handle streaming feedback with retry logic
  const streamFeedback = async (url, options = {}, retryAttempt = 0) => {
    setIsStreaming(true);
    setFeedback(''); // Clear existing feedback
    setError(null);

    try {
      const response = await retryWithBackoff(
        async () => {
          const res = await fetch(url, {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            const error = new Error(errorData.error || `HTTP ${res.status}`);
            error.status = res.status;
            throw error;
          }

          return res;
        },
        {
          maxRetries: 3,
          initialDelay: 1000,
          shouldRetry: (error) => error.status === 429 || (error.status >= 500 && error.status < 600)
        }
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedFeedback = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'chunk' && data.content) {
                streamedFeedback += data.content;
                setFeedback(streamedFeedback);
              } else if (data.type === 'complete') {
                // Update global feedback state
                if (setCurrentFeedback) {
                  const cleanFeedback = streamedFeedback.replace(/<[^>]*>/g, '') || '';
                  setCurrentFeedback(cleanFeedback);
                }
                setIsStreaming(false);
                return;
              } else if (data.type === 'error') {
                throw new Error(data.error || 'Streaming error');
              }
            } catch (parseError) {
              logger.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      logger.error('Streaming error:', error);
      setError(error.message || 'An error occurred during streaming');
      setIsStreaming(false);
    }
  };

  // Fetch feedback and chapter title when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      const fetchFeedbackAndTitle = async () => {
        setLoading(true);
        setError(null);
        
        try {
          // Fetch all chapters for navigation
          const chaptersRes = await fetch(`${import.meta.env.VITE_API_URL}/api/chapters?list=1`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          if (chaptersRes.ok) {
            const chaptersData = await chaptersRes.json();
            setChapters(chaptersData.sort((a, b) => a.order - b.order));
          }
          
          // First, fetch chapter details
          const chapterRes = await fetch(`${import.meta.env.VITE_API_URL}/api/chapters/${chapterId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (chapterRes.ok) {
            const chapterData = await chapterRes.json();
            setChapterTitle(chapterData.title);
            setChapterData(chapterData);
          } else {
            logger.error('Error fetching chapter title:', await chapterRes.text());
            setChapterTitle('Unknown Chapter');
          }
          
          // Fetch user progress to get questions and answers
          const progressRes = await fetch(`${import.meta.env.VITE_API_URL}/api/user/progress`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          if (progressRes.ok) {
            const progressData = await progressRes.json();
            const chapterProgress = progressData.progress?.[chapterId];
            
            if (chapterProgress && chapterProgress.answers && chapterData) {
              // Build Q&A array from chapter content blocks and user answers
              const qaArray = [];
              chapterData.contentBlocks.forEach(block => {
                if (block.blockType === 'question' || block.blockType === 'multi_choice') {
                  const answer = chapterProgress.answers[block.index];
                  if (answer !== undefined && answer !== null) {
                    const questionText = block.content?.prompt || block.content?.question || 'Question';
                    qaArray.push({
                      question: questionText,
                      answer: answer,
                      blockType: block.blockType,
                      options: block.content?.options || []
                    });
                  }
                } else if (block.blockType === 'exercise') {
                  // Include exercise/table blocks in Q&A
                  const answer = chapterProgress.answers[block.index];
                  if (answer !== undefined && answer !== null && typeof answer === 'object') {
                    const questionText = block.content?.textContent || 
                                       block.content?.prompt || 
                                       block.metadata?.helpText || 
                                       'Exercise/Table';
                    qaArray.push({
                      question: questionText,
                      answer: 'Table completed', // Text fallback
                      blockType: 'exercise',
                      tableData: answer // Full table data for rendering
                    });
                  }
                }
              });
              setQuestionsAndAnswers(qaArray);
            }
          }

          // Then try to get existing feedback first (non-streaming)
          const feedbackRes = await fetch(`${import.meta.env.VITE_API_URL}/api/feedback/${chapterId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (feedbackRes.ok) {
            const contentType = feedbackRes.headers.get('content-type');
            
            if (contentType && contentType.includes('text/event-stream')) {
              // Stream from this response's body (do not issue a second GET)
              setLoading(false);
              setIsStreaming(true);
              setFeedback('');
              setError(null);
              await readStreamFromResponse(
                feedbackRes,
                (streamedFeedback) => setFeedback(streamedFeedback),
                (streamedFeedback) => {
                  if (setCurrentFeedback) {
                    const cleanFeedback = streamedFeedback.replace(/<[^>]*>/g, '') || '';
                    setCurrentFeedback(cleanFeedback);
                  }
                  setIsStreaming(false);
                },
                (err) => {
                  setError(err?.message || 'Streaming error');
                  setIsStreaming(false);
                }
              );
            } else {
              // Handle regular JSON response (existing feedback)
              const feedbackData = await feedbackRes.json();
              setFeedback(feedbackData.feedback);
              
              // Set Q&A if provided
              if (feedbackData.questionsAndAnswers) {
                setQuestionsAndAnswers(feedbackData.questionsAndAnswers);
              }
              
              if (setCurrentFeedback) {
                const cleanFeedback = feedbackData.feedback?.replace(/<[^>]*>/g, '') || '';
                setCurrentFeedback(cleanFeedback);
              }
              setLoading(false);
            }
          } else {
            const errorData = await feedbackRes.json();
            logger.error('Error fetching feedback:', errorData.error);
            throw new Error(errorData.error || 'Failed to load feedback.');
          }

        } catch (error) {
          logger.error('Error:', error);
          setError(error.message || 'An unexpected error occurred.');
          setLoading(false);
        }
      };

      fetchFeedbackAndTitle();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [chapterId, isAuthenticated, token, authLoading, setCurrentFeedback]);

  const handleRegenerate = async () => {
    try {
      setError(null);
      await streamFeedback(`${import.meta.env.VITE_API_URL}/api/feedback/${chapterId}/regenerate`, {
        method: 'POST',
      });
    } catch (err) {
      logger.error('Error:', err);
      setError('An unexpected error occurred during regeneration.');
    }
  };

  // Only show spinner for auth loading or initial chapter title loading
  if (authLoading || (loading && !chapterTitle)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white dark:bg-gray-900">
        <div className="w-8 h-8 border-4 border-black dark:border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error && !feedback && !isStreaming) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <ErrorBoundary
            title="Feedback Error"
            message={error}
          >
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <p className="text-red-700 dark:text-red-300 mb-4">{error}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setError(null);
                    window.location.reload();
                  }}
                  className="bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 text-red-800 dark:text-red-200 px-4 py-2 rounded text-sm transition-colors"
                >
                  Retry
                </button>
                <button
                  onClick={() => navigate('/profile')}
                  className="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-sm transition-colors"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </ErrorBoundary>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-6xl mx-auto py-12 px-6">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-heading dark:text-white">
            Feedback: {formatDisplayTitle(chapterTitle) || 'Loading...'}
          </h1>
          <div className="flex gap-4">
            <button
              onClick={(e) => {
                e.preventDefault();
                navigate('/profile');
              }}
              className="bg-gray-500 dark:bg-gray-600 text-white px-4 py-2 rounded-lg text-lg font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
            >
              Back to Home
            </button>
            {feedback && !isStreaming && (
              <button
                onClick={() => exportFeedbackAsPdf(chapterTitle, questionsAndAnswers, feedback)}
                className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg text-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Download PDF
              </button>
            )}
            {(() => {
              // Find next chapter by curriculum order (unit → module → chapter)
              const sortedChapters = sortChaptersByCurriculumOrder(chapters);
              const currentIndex = sortedChapters.findIndex(ch => ch._id === chapterId);
              const nextChapter = currentIndex >= 0 && currentIndex < sortedChapters.length - 1 
                ? sortedChapters[currentIndex + 1] 
                : null;
              
              return nextChapter ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/chapter/${nextChapter._id}`);
                  }}
                  className="bg-black dark:bg-white text-white dark:text-black px-4 py-2 rounded-lg text-lg font-semibold hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                  title={nextChapter.title}
                >
                  Next Module: {nextChapter.title} →
                </button>
              ) : null;
            })()}
          </div>
        </div>
        {/* Display Questions and Answers */}
        {questionsAndAnswers.length > 0 && (
          <div className="mb-8 border border-gray-200 dark:border-gray-700 p-6 rounded-md bg-white dark:bg-gray-800">
            <h2 className="text-xl font-heading mb-4 dark:text-white">Your Responses</h2>
            <div className="space-y-6">
              {questionsAndAnswers.map((qa, idx) => (
                <div key={idx} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-b-0">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    <span className="block mb-1">Question {idx + 1}:</span>
                    <div className="font-normal prose prose-sm max-w-none dark:prose-invert prose-p:my-0.5 prose-headings:my-1">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                        {qa.question || ''}
                      </ReactMarkdown>
                    </div>
                  </div>
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Your Answer: </span>
                    {qa.blockType === 'exercise' && qa.tableData ? (
                      // Render exercise/table blocks as a non-editable table preview
                      <div className="mt-2 overflow-x-auto">
                        <table className="min-w-full w-full text-left border-collapse border border-gray-300 dark:border-gray-600">
                          <thead className="bg-gray-100 dark:bg-gray-700">
                            <tr>
                              {(qa.tableData.headings || []).map((heading, i) => (
                                <th
                                  key={i}
                                  className="px-4 py-2 border-b border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold text-sm"
                                >
                                  {heading}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                            {(qa.tableData.rows || []).map((row, rowIndex) => (
                              <tr
                                key={rowIndex}
                                className={`${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}`}
                              >
                                {(row || []).map((cell, colIndex) => (
                                  <td
                                    key={colIndex}
                                    className="px-4 py-2 border-b border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                                  >
                                    {cell || ''}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : Array.isArray(qa.answer) ? (
                      <span>{qa.answer.join(', ')}</span>
                    ) : typeof qa.answer === 'object' ? (
                      <span>{JSON.stringify(qa.answer)}</span>
                    ) : (
                      <span>{qa.answer}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="font-body border border-gray-200 dark:border-gray-700 p-6 rounded-md bg-white dark:bg-gray-800">
          {feedback ? (
            <div className="prose max-w-none dark:prose-invert prose-headings:dark:text-gray-100 prose-p:dark:text-gray-200 prose-table:dark:text-gray-200 prose-th:dark:text-gray-100 prose-td:dark:text-gray-300">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeSanitize]}
              >
                {feedback}
              </ReactMarkdown>
            </div>
          ) : isStreaming ? (
            <div className="flex items-center text-gray-500 dark:text-gray-400">
              <div className="w-4 h-4 border-2 border-black dark:border-white border-t-transparent rounded-full animate-spin mr-3"></div>
              Generating feedback...
            </div>
          ) : (
            <div className="flex items-center text-gray-500 dark:text-gray-400">
              <div className="w-4 h-4 border-2 border-black dark:border-white border-t-transparent rounded-full animate-spin mr-3"></div>
              Loading feedback...
            </div>
          )}

          {/* Show blinking cursor during streaming */}
          {isStreaming && feedback && (
            <span className="inline-block w-2 h-5 bg-black dark:bg-white animate-pulse ml-1"></span>
          )}

          {user?.role === 'admin' && feedback && !isStreaming && (
            <button
              onClick={handleRegenerate}
              className="mt-12 bg-black text-white px-4 py-2 rounded transition flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isStreaming}
            >
              {isStreaming ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-3 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                  Regenerating...
                </>
              ) : (
                'Regenerate Feedback'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Feedback;